import { Component } from '@theme/component';

/**
 * In-flight and recent upsell responses keyed by request, so the desktop panel
 * and mobile row share one fetch per cart change.
 * @type {Map<string, Promise<string>>}
 */
const responses = new Map();
const MAX_CACHED_RESPONSES = 6;

/**
 * Fetches upsell section HTML, reusing a pending or recent response for the same key.
 * @param {string} url
 * @param {string} key - The URL plus the cart signature, since in-cart products are filtered server-side
 * @param {AbortSignal} signal
 * @returns {Promise<string>}
 */
function fetchUpsell(url, key, signal) {
  let response = responses.get(key);
  if (!response) {
    response = fetch(url).then((result) => {
      if (!result.ok) throw new Error(`Cart upsell request failed: ${result.status}`);
      return result.text();
    });
    response.catch(() => responses.delete(key));
    responses.set(key, response);

    // Keep the cache small; Map preserves insertion order
    if (responses.size > MAX_CACHED_RESPONSES) {
      const oldest = responses.keys().next().value;
      if (oldest !== undefined) responses.delete(oldest);
    }
  }

  // Abort only this consumer's wait, not the shared request
  return new Promise((resolve, reject) => {
    signal.addEventListener('abort', () => reject(signal.reason), { once: true });
    response.then(resolve, reject);
  });
}

/**
 * Shows product recommendations for the most recently added cart item.
 *
 * Rendered by `snippets/cart-upsell.liquid` inside the cart drawer with
 * `data-skip-subtree-update`, so cart re-renders keep the fetched cards and
 * only update `data-product-id` / `data-cart-signature`, which trigger a refetch.
 *
 * @extends {Component}
 */
class CartUpsell extends Component {
  static get observedAttributes() {
    return ['data-product-id', 'data-cart-signature'];
  }

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #loadScheduled = false;

  connectedCallback() {
    super.connectedCallback();
    this.#scheduleLoad();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (this.isConnected && oldValue !== null && oldValue !== newValue) this.#scheduleLoad();
  }

  /**
   * A cart re-render can change both observed attributes in one morph; coalesce them into one request.
   */
  #scheduleLoad() {
    if (this.#loadScheduled) return;
    this.#loadScheduled = true;
    queueMicrotask(() => {
      this.#loadScheduled = false;
      if (this.isConnected) this.#load();
    });
  }

  async #load() {
    const { productId, url, cartSignature = '' } = this.dataset;
    if (!productId || !url) return;

    // Cancel the previous request so an older response cannot replace newer cart state
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    const requestUrl = `${url}&product_id=${encodeURIComponent(productId)}`;

    try {
      const html = await fetchUpsell(requestUrl, `${requestUrl}|${cartSignature}`, abortController.signal);
      if (abortController.signal.aborted) return;

      const responseDocument = new DOMParser().parseFromString(html, 'text/html');
      const results = responseDocument.querySelector('[data-cart-upsell-results]');
      // The default product card's CSS ships in the response's section stylesheet, which
      // is missing on pages that render no product cards, so keep it with the cards.
      const stylesheet = responseDocument.querySelector('style[data-section-stylesheet]');
      // Rename the marker so section-renderer's stylesheet injection for the header never targets this copy
      stylesheet?.removeAttribute('data-section-stylesheet');
      stylesheet?.setAttribute('data-cart-upsell-stylesheet', '');

      // The response holds both card styles; keep only this layout's list so forms, IDs and
      // live regions are not duplicated between the desktop panel and the mobile row.
      const unusedList = this.dataset.layout === 'panel' ? '.cart-upsell__list--compact' : '.cart-upsell__list--stack';
      results?.querySelector(unusedList)?.remove();

      // Upsell cards always add directly. Switch quick add to its "add" state (base.css then
      // shows the Add button) and drop "Choose", which inline styles can otherwise keep visible.
      for (const quickAdd of results?.querySelectorAll('quick-add-component') ?? []) {
        if (!(quickAdd instanceof HTMLElement)) continue;
        quickAdd.dataset.quickAddButton = 'add';
        quickAdd.querySelector('.quick-add__button--choose')?.remove();
      }

      // CSS shows the container only while it holds `.cart-upsell__item` cards
      this.replaceChildren(...(stylesheet ? [stylesheet] : []), ...(results ? results.childNodes : []));
    } catch (error) {
      if (abortController.signal.aborted) return;
      this.replaceChildren();
      console.error(error);
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
